import { motion, type Variants } from 'framer-motion';
import { HelpCircle, Key, Shield, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { verify2FALogin } from '../auth-client';
import { OTPInput } from './OTPInput';

interface TwoFactorVerifyProps {
  tempToken: string;
  onComplete: (user: any) => void;
  onError: (error: string) => void;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const },
  },
};

export function TwoFactorVerify({
  tempToken,
  onComplete,
  onError,
}: TwoFactorVerifyProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [otpCode, setOtpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      setError('Por favor, digite o código de 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verify2FALogin(tempToken, otpCode);
      onComplete(response.user);
    } catch (err: any) {
      setError(err.message || 'Código inválido. Verifique e tente novamente.');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyBackupCode = async () => {
    if (!backupCode.trim()) {
      setError('Por favor, digite um código de backup');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verify2FALogin(tempToken, backupCode.trim());
      onComplete(response.user);
    } catch (err: any) {
      setError(err.message || 'Código de backup inválido.');
      setBackupCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[hsl(210,35%,32%)] to-[hsl(210,40%,25%)] px-4">
      <motion.div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div variants={itemVariants} className="mb-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(210,35%,32%)] to-[hsl(210,40%,25%)] flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[hsl(210,50%,10%)] mb-2">
            Verificação de Dois Fatores
          </h1>
          <p className="text-[hsl(215,16%,47%)] text-sm">
            Digite o código do seu aplicativo autenticador
          </p>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6"
          >
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-700">
                {error}
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        {!useBackupCode ? (
          <div className="space-y-6">
            <motion.div variants={itemVariants} className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-[hsl(215,16%,47%)]">
                <Smartphone className="w-4 h-4" />
                <span className="text-sm">Código do aplicativo</span>
              </div>
              <OTPInput
                value={otpCode}
                onChange={setOtpCode}
                error={!!error}
                disabled={loading}
                onComplete={handleVerifyOTP}
              />
            </motion.div>

            <motion.div variants={itemVariants}>
              <Button
                onClick={handleVerifyOTP}
                className="w-full h-11 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                disabled={otpCode.length !== 6 || loading}
              >
                {loading ? 'Verificando...' : 'Verificar'}
              </Button>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="pt-4 border-t border-[hsl(214,32%,91%)]"
            >
              <button
                onClick={() => setUseBackupCode(true)}
                className="w-full flex items-center justify-center gap-2 text-sm text-[hsl(215,16%,47%)] hover:text-[hsl(210,35%,32%)] transition-colors"
              >
                <Key className="w-4 h-4" />
                Usar código de backup
              </button>
            </motion.div>
          </div>
        ) : (
          <div className="space-y-6">
            <motion.div variants={itemVariants} className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-[hsl(215,16%,47%)]">
                <Key className="w-4 h-4" />
                <span className="text-sm">Código de backup</span>
              </div>

              <Alert className="border-[hsl(214,32%,91%)] bg-[hsl(210,40%,98%)]">
                <HelpCircle className="h-4 w-4 text-[hsl(215,16%,47%)]" />
                <AlertDescription className="text-[hsl(215,16%,47%)] text-sm">
                  Digite um dos códigos de backup salvos durante a configuração.
                  Cada código só pode ser usado uma vez.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label
                  htmlFor="backupCode"
                  className="text-[hsl(210,50%,10%)] font-medium text-sm"
                >
                  Código de backup
                </Label>
                <Input
                  id="backupCode"
                  type="text"
                  placeholder="Digite seu código de backup"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value)}
                  disabled={loading}
                  className={`h-11 bg-[hsl(210,40%,98%)] border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] placeholder:text-[hsl(215,16%,47%)] focus:border-[hsl(43,56%,61%)] focus:ring-[hsl(43,56%,61%)] transition-all ${
                    error
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : ''
                  }`}
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setUseBackupCode(false);
                  setBackupCode('');
                  setError('');
                }}
                className="flex-1 h-11 border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,98%)]"
                disabled={loading}
              >
                Voltar
              </Button>
              <Button
                onClick={handleVerifyBackupCode}
                className="flex-1 h-11 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                disabled={!backupCode.trim() || loading}
              >
                {loading ? 'Verificando...' : 'Verificar'}
              </Button>
            </motion.div>
          </div>
        )}

        <motion.div
          variants={itemVariants}
          className="mt-6 pt-6 border-t border-[hsl(214,32%,91%)] text-center"
        >
          <p className="text-sm text-[hsl(215,16%,47%)] mb-2">
            Não consegue acessar?
          </p>
          <button
            onClick={handleBackToLogin}
            className="text-sm text-[hsl(215,16%,47%)] hover:text-[hsl(210,35%,32%)] transition-colors"
          >
            Voltar ao login
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
